import React, { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import InfoView from 'sharedComponents/InfoView';
import InfoViewSkeleton from 'sharedComponents/InfoViewSkeleton';

import { alphabetizeClients } from 'sharedHelpers/clientHelpers';

import useClientListHydration from 'sharedHooks/useClientListHydration';
import useFilterClients from 'sharedHooks/useFilterClients';
import useNavigateToClient from 'sharedHooks/useNavigateToClient';

import ClientListPanelItem from './components/ScheduleListPanelItem';
import ScheduleDetailPanel from './components/ScheduleInfoPanel';
import AddEventButton from './components/AddEventButton';
import Dashboard from './components/Dashboard';
import { EditEventDrawerProvider } from './components/EditEventDrawerProvider';
import { CheckInResponseDrawerProvider } from './components/CheckInResponseDrawerProvider';

interface SchedulePageProps {
  clientId?: string;
}

function SchedulePage({ clientId }: SchedulePageProps): JSX.Element {
  const { showScheduleDashboardFlag } = useFlags();

  const {
    clients,
    loading,
    isBackgroundLoadingClients,
  } = useClientListHydration();

  const filteredClientsList = useFilterClients({
    clientsList: clients,
    sortFunction: alphabetizeClients,
  });

  const navigateToClient = useNavigateToClient();

  useEffect(() => {
    if (!showScheduleDashboardFlag && !clientId && filteredClientsList.length) {
      navigateToClient(filteredClientsList[0].id);
    }
  }, [
    showScheduleDashboardFlag,
    clientId,
    filteredClientsList.length,
    navigateToClient,
  ]);

  return (
    <EditEventDrawerProvider>
      <CheckInResponseDrawerProvider>
        {loading && <InfoViewSkeleton />}
        {!loading && (
          <InfoView
            selectedItem={clientId}
            listPanelChildren={({ index, style }) => (
              <ClientListPanelItem
                clientsData={filteredClientsList}
                style={style}
                index={index}
              />
            )}
            detailPanelChildren={
              clientId ? (
                <ScheduleDetailPanel clientId={clientId} />
              ) : (
                showScheduleDashboardFlag && <Dashboard />
              )
            }
            title="Schedule"
            loadingMessage={
              isBackgroundLoadingClients ? 'Loading more clients…' : undefined
            }
            numberOfItems={filteredClientsList.length}
            HeaderButton={<AddEventButton defaultClientId={clientId} />}
            errorMessage={
              clients.length > filteredClientsList.length
                ? 'There are no clients matching the current filters'
                : 'You have no clients to display'
            }
          />
        )}
      </CheckInResponseDrawerProvider>
    </EditEventDrawerProvider>
  );
}

SchedulePage.defaultProps = {
  clientId: undefined,
};

export default SchedulePage;
